import { Link, ButtonGroup, Flex } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import type { UseBankConnectState } from "bankconnect";
import { useCallback } from "react";
import { useIntl } from "react-intl";
import { getPlatformStringFromConnectionCategory, getReconnectConnectionDetailsText } from "shared-ui";

import { COMMON_MESSAGES } from "../../../common-messages";
import { type ReconnectionStep } from "../util";

interface ReconnectLandingProps {
  airData: FundingApplicationSchemas.ReconnectConnectionAIRSchema;
  useBankConnect: () => UseBankConnectState;
  setCurrentReconnectStep: (step: ReconnectionStep) => void;
  onBack: () => void;
}

export const ReconnectLanding = ({
  airData,
  useBankConnect,
  setCurrentReconnectStep,
  onBack,
}: ReconnectLandingProps) => {
  const intl = useIntl();
  const { startReconnect, bankConnections } = useBankConnect();

  const triggerSadPath = useCallback(() => {
    if (airData.connection_meta_data?.type === "BANKING") {
      setCurrentReconnectStep("cannot_reconnect_bank");
    }
    // Todo - handle other connection types
  }, [airData.connection_meta_data?.type, setCurrentReconnectStep]);

  const triggerReconnect = useCallback(() => {
    if (airData.connection_meta_data?.type === "BANKING") {
      const airMetaData = airData.connection_meta_data as FundingApplicationSchemas.ReconnectBankConnectionMetaData;
      if (!airMetaData.public_connection_ids) {
        return new Error("No bank connection ids to reconnect");
      }

      const bankConnection = bankConnections.find((connection) => {
        // Supports plaid only for now (will support basiq once we have a reconnect flow implemented)
        return airMetaData.public_connection_ids && connection.id === airMetaData.public_connection_ids[0];
      });

      if (bankConnection) {
        switch (bankConnection.providerType) {
          case "plaid":
            return startReconnect(bankConnection);
          default:
            return new Error(`AIR reconnect flow for ${bankConnection.providerType} not supported`);
        }
      }
    }
    return;
  }, [airData.connection_meta_data, bankConnections, startReconnect]);

  // Todo: Implement differently depending on reconnection type
  const isSubmitting = false;

  return (
    <Flex direction="column" gap="8">
      {getReconnectConnectionDetailsText(airData)}
      <ButtonGroup
        primaryAction={{
          onAction: () => {
            // Trigger the reconnect process
            triggerReconnect();
          },
          content: intl.formatMessage({
            id: "funding-application: reconnect connection air reconnect button label",
            defaultMessage: "Reconnect",
          }),
          analyticsId: "reconnect-connection-trigger-reconnection-button",
          loading: isSubmitting,
        }}
        secondaryAction={{
          onAction: onBack,
          content: intl.formatMessage(COMMON_MESSAGES.backLabel),
          analyticsId: "reconnect-connection-back-button",
        }}
        primaryActionTestId="navigation-continue-button"
        secondaryActionTestId="navigation-back-button"
      />
      {/* Todo: Sad path behaviour */}
      <Link
        onClick={() => triggerSadPath()}
        data-dd-action-name={`reconnect-connection-cannot-reconnect-${airData.connection_type}-button`}
      >
        {intl.formatMessage(
          {
            id: "funding-application: air cannot connect platform button label",
            defaultMessage: "I can't connect my {platform}",
            description:
              "Button user can click if they cannot connect their platform. Platform here refers to the platorm category e.g. bank accounts, eCommerce platform, ect.",
          },
          {
            platform: getPlatformStringFromConnectionCategory(
              intl,
              airData.connection_meta_data?.type,
              airData.connection_type
            ),
          }
        )}
      </Link>
    </Flex>
  );
};
